import {
    EvaluateGradeModel,
    GradeEvaluationModel,
    GradeScaleBandWriteModel,
    GradeScaleEvaluationMode,
    GradeScaleModel,
    GradeScaleWriteModel,
    IEvaluateGradeModel,
    SetMarkTypeGradeScaleModel,
} from "../api/APIClient";
import { gradeScalesClient } from "../api/clients";

/**
 * Grade scales: the bands that turn a numeric result into a symbol.
 *
 * The load-bearing rule this store exists to respect: a symbol is NEVER persisted on a mark. It is
 * resolved at read time, procedure-version override first, then the mark type's default scale. So
 * there is no "set the grade on this result" call here and there should never be one - changing a
 * band changes what every historical mark displays, by design.
 */

export type { GradeScaleModel };
export { GradeScaleEvaluationMode };

/**
 * Plain-object input shapes for writes.
 *
 * The generated `IGradeScaleWriteModel` types its nested `bands` as the *class*
 * `GradeScaleBandWriteModel`, not the interface, so an object literal never satisfies it. Rather
 * than making every screen build class instances for a nested collection, the store declares what it
 * accepts and does the conversion in one place.
 */
export interface GradeScaleBandInput {
    code: string;
    name: string;
    minimumValue?: number;
    numericValue?: number;
    isPass?: boolean;
}

export interface GradeScaleInput {
    name: string;
    evaluationMode?: GradeScaleEvaluationMode;
    bands?: GradeScaleBandInput[];
}

export const listGradeScales = (includeArchived = false): Promise<GradeScaleModel[]> => gradeScalesClient.list(includeArchived);

export const getGradeScale = (id: string): Promise<GradeScaleModel> => gradeScalesClient.get(id);

const toWriteModel = (model: GradeScaleInput): GradeScaleWriteModel =>
    new GradeScaleWriteModel({
        name: model.name,
        evaluationMode: model.evaluationMode,
        bands: (model.bands ?? []).map((b) => new GradeScaleBandWriteModel(b)),
    });

export const createGradeScale = (model: GradeScaleInput): Promise<GradeScaleModel> => gradeScalesClient.create(toWriteModel(model));

export const updateGradeScale = (id: string, model: GradeScaleInput): Promise<GradeScaleModel> =>
    gradeScalesClient.update(id, toWriteModel(model));

export const archiveGradeScale = (id: string): Promise<GradeScaleModel> => gradeScalesClient.archive(id);

export const deleteGradeScale = (id: string): Promise<boolean> => gradeScalesClient.delete(id);

/** Attach (or clear, by passing undefined) the default scale for a mark type. */
export const setMarkTypeGradeScale = (markTypeID: string, gradeScaleID?: string): Promise<boolean> =>
    gradeScalesClient.setMarkTypeDefault(markTypeID, new SetMarkTypeGradeScaleModel({ gradeScaleID }));

/** Ask the server what symbol a result would produce. Used by the band tester on the grading screen. */
export const evaluateGrade = (model: IEvaluateGradeModel): Promise<GradeEvaluationModel> =>
    gradeScalesClient.evaluate(new EvaluateGradeModel(model));

/** Human label for the evaluation mode enum, which serializes as a number. */
export function evaluationModeLabel(mode?: GradeScaleEvaluationMode): string {
    return mode === GradeScaleEvaluationMode.NormalizedValue ? "Normalized (percentage)" : "Raw value";
}
